import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 7.4 visual preview at /preview/lesson/7.4
 * "Disagreeing about AI without burning the room"
 * Frame: how to turn an AI disagreement into a decision.
 */

export default function LessonPreviewPage7_4() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromPrev />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 7 · Working with people who use AI differently · Lesson 7.4
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        Disagreeing about AI without burning the room
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Frame · Decision · De-escalation</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 reframe-the-fight drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="The same disagreement. Two different framings. One ends in a decision. One ends in a fight.">
      <p className="mt-6 text-text/90">
        Team meeting on a Tuesday. Five people. You are deciding whether the
        team should use AI to draft the client-facing recap email after each
        weekly call. Two people are strongly for it. Two are strongly
        against. The fifth is silent.
      </p>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.06] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            Framing A · Ends in a fight
          </p>
          <p className="mt-3 text-text/90 italic">
            &ldquo;I think people who refuse to use AI are going to fall
            behind. We need to get with the times. Are we doing this or not?&rdquo;
          </p>
          <p className="mt-3 text-sm text-text/85">
            Three sentences. Implicit insult to the people across the table.
            A forced binary. The meeting goes 40 minutes, lands nowhere, and
            two people are quietly furious for the rest of the week.
          </p>
        </div>
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.06] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            Framing B · Ends in a decision
          </p>
          <p className="mt-3 text-text/90 italic">
            &ldquo;We have a real disagreement about AI on the recap email.
            Can we make this a one-question decision: does the recap email
            need to be in the client&apos;s exact voice from the call, or is
            a clean summary fine? If voice, we draft by hand. If summary,
            AI&rsquo;s fine as a starting point. I&rsquo;ll go first.&rdquo;
          </p>
          <p className="mt-3 text-sm text-text/85">
            Same disagreement. The fight became a question. The question has
            an answer. The meeting ends in twelve minutes with a decision
            everyone can live with.
          </p>
        </div>
      </div>

      <p className="mt-6 text-text/90">
        Lesson 7.4 is the move from framing A to framing B. The move is
        called Frame, and it is the move that decides whether AI
        disagreements on your team produce decisions or grudges.
      </p>
    </Section>
  );
}

/* --- Section 2: From 7.3 --- */
function FromPrev() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · Lesson 7.3" title="7.3 was the one-on-one with a refuser. 7.4 is the whole room disagreeing.">
      <p className="mt-6 text-text/90">
        Lesson 7.3 trained you to build a carve-out with one teammate who has
        drawn a line. Lesson 7.4 zooms out. Now the team is split. Three
        people see AI one way, two see it another, and there is a real
        decision the team has to make: should we use AI here, and if so, how.
      </p>
      <p className="mt-4 text-text/90">
        Most teams fail this conversation the same way: they argue about AI
        in general instead of deciding about AI in this one case. General
        arguments do not end. Specific decisions do.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="Define Carve-out from 7.3 in one sentence." />
        <ReviewItem n="02" q="The honesty rule from 7.3, in one line." />
        <ReviewItem n="03" q="Why a general AI argument never ends." />
      </ol>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · Frame the disagreement" title="The move that turns a values argument into a decision a team can actually make.">
      <Subhead n={1} title="The vocab for the lesson" />
      <VocabCard
        term="Frame"
        definition="A short, specific reformulation of an AI disagreement that turns it from a values argument into a concrete question the team can answer in this meeting. A good frame names the actual decision (one task, one artifact, one moment), proposes a criterion the room can apply, and ends the conversation in minutes instead of weeks."
        analogy="Two siblings can fight forever about whose turn it is to pick the movie. Or one of them can say: &ldquo;Whoever did dishes last picks. Who did dishes last?&rdquo; Same conflict. Different frame. The fight had no end. The question has one. AI disagreements work the same way: the values fight is endless, the specific question almost always has an answer the room can live with."
        memoryHook="A frame turns &lsquo;should we use AI&rsquo; into &lsquo;should we use AI on this thing, judged by this criterion, by Friday.&rsquo; The first cannot be answered. The second can."
        why="Most AI disagreements on real teams are not about AI. They are about voice, accuracy, fairness, time, or who is doing the work. Framing surfaces the real question and lets the team decide it. Without framing, you have a values war that produces no decision and a lot of resentment."
      />

      <Subhead n={2} title="The four parts of a good frame" className="mt-12" />
      <p className="mt-4 text-text/90">
        Each part is something the frame has to do. A frame that skips one
        of these will collapse back into the original argument within five
        minutes.
      </p>
      <PartsGrid />

      <Subhead n={3} title="Three reframes that almost always work" className="mt-12" />
      <p className="mt-4 text-text/90">
        Most AI disagreements collapse onto one of three frames. If you can
        recognize which one is underneath, you can move the room there fast.
      </p>
      <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          The three reframes
        </p>
        <ol className="mt-3 space-y-3 text-text/90">
          <li><span className="font-mono text-xs text-amber mr-2">01.</span> &ldquo;Is this a voice decision or an output decision?&rdquo; If voice (the work has to sound like a specific person), human draft. If output (the work just has to be accurate and readable), AI draft plus a human pass.</li>
          <li><span className="font-mono text-xs text-amber mr-2">02.</span> &ldquo;What is the cost if we are wrong, and who pays it?&rdquo; High cost to a specific person (a client, a teacher, a recipient): tighten the workflow. Low cost: ship faster.</li>
          <li><span className="font-mono text-xs text-amber mr-2">03.</span> &ldquo;Is the disagreement about this task or about AI in general?&rdquo; If general, table it for a separate conversation and decide the task in front of you with a one-time rule.</li>
        </ol>
      </div>

      <Subhead n={4} title="Where AI helps and where AI hurts on framing" className="mt-12" />
      <p className="mt-4 text-text/90">
        AI is genuinely useful before the meeting. Inside the meeting it is
        worse than useless: pulling out your phone to ask AI mid-disagreement
        will end the conversation, but not in the direction you wanted.
      </p>
      <SplitGrid />

      <Subhead n={5} title="The two-minute test" className="mt-12" />
      <p className="mt-4 text-text/90">
        A good frame can be stated in two minutes or less. If it takes longer
        to set up than that, it is not a frame, it is a speech. People will
        tune out and you will be back in the same argument by the third
        minute.
      </p>
      <div className="mt-6 rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
          The shape
        </p>
        <p className="mt-3 text-text/90">
          One sentence naming the real question. One sentence proposing the
          criterion. One sentence saying when the decision needs to land.
          That is it. Three sentences, plus an invitation to anyone else to
          reframe further before you commit.
        </p>
      </div>
    </Section>
  );
}

const PARTS = [
  {
    n: '01',
    name: 'Name the specific task',
    body: 'Not “AI on the team” but “AI on the Friday client recap email.” The narrower the task, the easier the decision. Big decisions made in general arguments tend not to stick anyway.',
  },
  {
    n: '02',
    name: 'Propose a criterion the room can apply',
    body: 'The criterion is what makes the question answerable. “Does this need to sound like a specific person, or just be accurate?” is a criterion. “Is AI good or bad?” is not. Bring the criterion. Do not wait for someone else to.',
  },
  {
    n: '03',
    name: 'Set a deadline for the decision',
    body: 'Open-ended team conversations about AI never close. “Let\'s decide by end of meeting” or “by Friday standup” gives the conversation an edge. A frame without a deadline becomes a recurring fight.',
  },
  {
    n: '04',
    name: 'Invite a counter-frame before you commit',
    body: 'After you propose your frame, ask: “Is there a better question we should be answering instead?” Sometimes there is, and the right counter-frame saves the next hour. If nobody has one, the frame stands and the room can decide.',
  },
];

function PartsGrid() {
  return (
    <div className="mt-6 space-y-4">
      {PARTS.map((p) => (
        <div key={p.n} className="rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-mono text-xs text-muted">Part {p.n}</span>
            <h4 className="text-lg font-semibold text-amber">{p.name}</h4>
          </div>
          <p
            className="mt-3 text-text/90"
            dangerouslySetInnerHTML={{ __html: p.body }}
          />
        </div>
      ))}
    </div>
  );
}

const SPLIT = [
  {
    side: 'AI helps',
    items: [
      'Before the meeting, drafting three or four possible frames so you walk in with options.',
      'Pressure-testing your frame: would the other side accept this as fair? If not, why?',
      'Generating the criterion you might use (voice vs output, cost vs speed, this task vs general).',
      'After the meeting, writing down the decision in one line so it does not have to be re-litigated.',
    ],
    accent: 'emerald' as const,
  },
  {
    side: 'AI hurts',
    items: [
      'Pulling out your phone mid-meeting to ask AI for a rebuttal. This ends the conversation, badly.',
      'Generating zingers or comebacks. AI is bad at this and the room can always tell.',
      'Letting AI tell you who is right. AI does not know your team and was not in the room.',
      'Producing a long speech instead of a short frame. AI defaults to over-explaining.',
    ],
    accent: 'rose' as const,
  },
];

function SplitGrid() {
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      {SPLIT.map((col) => {
        const isEm = col.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-rose-500/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-rose-500/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-rose-300';
        return (
          <div key={col.side} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
              {col.side}
            </p>
            <ul className="mt-3 space-y-2">
              {col.items.map((it, i) => (
                <li key={i} className="flex gap-2 text-sm text-text/90">
                  <span className={`shrink-0 ${labelCls}`}>·</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Pick the frame that produces a decision" title="Three rooms in mid-disagreement. Two reframes. Pick the one that turns the fight into a decision.">
      <p className="mt-6 text-text/90">
        Each scene shows a team in the middle of an AI argument that is not
        going anywhere. Both reframes are honest attempts. Only one matches
        the shape of a real frame: specific task, working criterion, clear
        deadline, room to counter-frame.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <FrameDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

type DrillSpec = {
  n: string;
  scene: string;
  optionA: string;
  optionB: string;
  correct: 'A' | 'B';
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    scene:
      'High school newspaper editorial team. Three editors want to use AI to draft article summaries. Two editors are loudly against it. The conversation has been going for 30 minutes. Tonight\'s issue still needs to ship.',
    optionA:
      '“Look, AI is here to stay. We need to figure out how we feel about it as a team. Maybe we should do a workshop on AI ethics next week before we keep going on this.”',
    optionB:
      '“Different question. Tonight: do the article summaries need to be in the reporter\'s voice, or just accurate and short? If voice, the reporter writes the summary. If short and accurate, AI drafts and the reporter signs off. We pick tonight, revisit in two weeks. Better question I’m missing?”',
    correct: 'B',
    verdict:
      'Option B has all four parts of a frame: specific task (tonight\'s summaries), working criterion (voice vs short-and-accurate), deadline (tonight, with a revisit), and an invitation to counter-frame. The meeting can end in five minutes. Option A pushes the decision out to a workshop, defaults the team to no decision tonight, and guarantees the same fight next week.',
  },
  {
    n: '02',
    scene:
      'Small startup, four people. The founder wants to use AI to write all sales emails to keep the volume up. The salesperson hired last month says AI emails feel impersonal and is worried they will hurt response rates. The founder is getting frustrated. The conversation is starting to sound like a values argument.',
    optionA:
      '“Honestly, every company is using AI for sales now. If we don\'t, we are going to get left behind. Let\'s just trust the process and ship the AI emails this week.”',
    optionB:
      '“Real disagreement here. Let\'s narrow it: just the cold outbound emails for two weeks. Half AI-drafted, half hand-written. Same recipients by territory. Compare response rates Aug 15. Whoever wins, we scale that approach. Fair test?”',
    correct: 'B',
    verdict:
      'Option B names a specific task (cold outbound emails), proposes a measurable criterion (response rates), sets a deadline (Aug 15), and invites a counter (“fair test?”). The room gets a decision rule both sides can live with because both sides get a real shot. Option A is the founder pulling rank under a generic appeal to industry pressure. The salesperson will quietly disengage from cold email for the rest of her tenure.',
  },
  {
    n: '03',
    scene:
      'Nonprofit board meeting. The team is split over whether to use AI in donor thank-you letters. One side: it saves time and gets more letters out. Other side: donors will feel hurt if they find out. The meeting has 15 minutes left and three other agenda items.',
    optionA:
      '“Voice or output question? Thank-you letters are voice. Donors gave because they trust this org, so the letter has to sound like a person from here. Recommend hand-written, AI for envelope addresses and tracking. Five-minute decision; if anyone wants to counter, now is the time.”',
    optionB:
      '“You know what, this is a really important question and I don\'t think 15 minutes is enough. Let\'s pull together a subcommittee, do some research, and come back to the full board next quarter with a recommendation.”',
    correct: 'A',
    verdict:
      'Option A uses the voice-vs-output reframe (one of the three from the lesson), makes a concrete proposal (hand-written for the letter, AI for logistics), and gives the room a five-minute window plus an invitation to counter. Decision lands inside the meeting. Option B is the classic nonprofit move: punt to a subcommittee that meets once and forgets. The decision never gets made and the thank-you letters keep going out in whatever default the executive director defaults to.',
  },
];

function FrameDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<'A' | 'B' | null>(null);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The scene
      </p>
      <p
        className="mt-3 text-text/90"
        dangerouslySetInnerHTML={{ __html: drill.scene }}
      />

      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Which reframe produces a decision?
        </p>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {(['A', 'B'] as const).map((key) => {
            const text = key === 'A' ? drill.optionA : drill.optionB;
            const isPicked = picked === key;
            const isCorrect = key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'rounded-md border border-line bg-elevated p-4 text-left text-sm text-text/85 hover:border-amber transition-colors';
            if (shouldShowCorrect)
              cls =
                'rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-4 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'rounded-md border border-rose-500 bg-rose-500/[0.08] p-4 text-left text-sm text-text';
            return (
              <button
                key={key}
                className={cls}
                onClick={() => setPicked(key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Option {key}
                </span>
                <span
                  className="mt-2 block italic"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              </button>
            );
          })}
        </div>

        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · Option {picked}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That one pushes the decision out or pulls rank instead of
              naming the task and the criterion. The same argument will be
              back next week.
            </p>
          </div>
        )}

        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              {wasWrong ? 'The frame that produces a decision' : 'Correct'} · Option {drill.correct}
            </p>
            <p className="mt-2 text-sm text-text/90">{drill.verdict}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define Frame in one sentence." answer="A short, specific reformulation of an AI disagreement that turns it from a values argument into a concrete question the team can answer in this meeting." />
        <QuickCheckItem n="Q2" q="The four parts of a good frame." answer="Name the specific task. Propose a criterion the room can apply. Set a deadline for the decision. Invite a counter-frame before you commit." />
        <QuickCheckItem n="Q3" q="The three reframes that almost always work." answer="Voice vs output. What is the cost if we are wrong, and who pays it. This task vs AI in general." />
        <QuickCheckItem n="Q4" q="Two things AI helps with on framing. Two things AI hurts." answer="Helps: drafting frame options before the meeting, pressure-testing fairness, generating criteria, writing down the decision in one line afterward. Hurts: pulling AI out mid-meeting for a rebuttal, generating zingers, letting AI decide who is right, producing a speech instead of a frame." />
        <QuickCheckItem n="Q5" q="The two-minute test, in one line." answer="A good frame can be stated in two minutes or less. If it takes longer to set up than that, it is a speech, not a frame." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. The frame map.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 7.4 · Lock card · Disagreeing about AI
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          A frame turns &ldquo;should we use AI&rdquo; into &ldquo;should we
          use AI on this thing, judged by this criterion, by Friday.&rdquo;
          The first cannot be answered. The second can.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <LockTile n="01" name="Specific task" body='Not "AI in general." Pick the one artifact or moment in front of you.' />
          <LockTile n="02" name="Working criterion" body='Bring the question that decides it. "Voice or output?" "Cost if we are wrong?"' />
          <LockTile n="03" name="Deadline" body="By end of meeting. By Friday standup. Open-ended AI arguments never close." />
          <LockTile n="04" name="Counter-frame welcome" body={'"Better question I’m missing?" Lets the room land somewhere better if it can.'} />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The three reframes that almost always work
          </p>
          <p className="mt-2 text-text/90">
            Voice or output? Cost if we are wrong, and who pays? This task
            vs AI in general (and table the general for another day).
          </p>
        </div>

        <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            The two-minute shape
          </p>
          <p className="mt-2 text-text/90">
            One sentence: the real question. One sentence: the criterion. One
            sentence: the deadline. Plus the invitation to counter-frame.
            That is the whole frame.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          Most AI fights are not about AI. Frame the actual question and
          watch the room exhale.
        </p>
      </div>
    </Section>
  );
}

function LockTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p
        className="mt-3 text-sm text-text/85"
        dangerouslySetInnerHTML={{ __html: body }}
      />
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 7.5 · Week 7 capstone
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            The shared standard: writing the team&apos;s AI ground rules
          </h3>
          <p className="mt-3 text-sm text-text/85">
            Vocab: Standard. Take everything from Week 7 and ship a one-page
            AI standard for a team you are actually on. Real artifact, real
            team, real conversation.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 7.4 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p
          className="mt-3 border-t border-line pt-3 text-sm text-text/85"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    'From 7.3',
    'The frame',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
